import {Button, Col, DatePicker, Form, Modal, notification, Row, Select, Checkbox, List} from "antd";
import React, {useState, useEffect} from "react";
import { useTranslation } from "react-i18next";
import { coreApi } from "../../api/calls";
import TextArea from "antd/lib/input/TextArea";
import EmployeeSelect from "../Employees/EmployeeSelect";
import PartnerSelect from "../Partners/PartnerSelect";
import moment from "moment/moment";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import "./ContactAddCommunication.css";

const gutter = [8, 0];
const MAX_CHARACTERS = 15000;
const MIN_CHARACTERS = 10;
const communication_cases = ['phone', 'email'];
const communications = ["review", "verification", "finance", "operational", "contract", "other"];

const ContactAddCommunication = ({ contactId, buildings = [], onUpdate }) => {
  const { t } = useTranslation();

  const [isModalOpen, setModalOpen] = useState(false);
  const [text, setText] = useState('');
  const [selectedBuildings, setSelectedBuildings] = useState([]);
  const [buildingPartners, setBuildingPartners] = useState({});
  const [partners, setPartners] = useState([]);
  const [form] = Form.useForm();

  const findBuildingPartnerId = (building) => {
    if (!building.building_units || building.building_units.length === 0) return null;
    
    for (const unit of building.building_units) {
      if (!unit.service_rrules || unit.service_rrules.length === 0) continue;
      
      const lastServiceRule = unit.service_rrules[unit.service_rrules.length - 1];
      if (lastServiceRule?.partner_id) {
        return lastServiceRule.partner_id;
      }
    }
    return null;
  };

  useEffect(() => {
    const initialPartners = {};
    buildings.forEach(building => {
      const partnerId = findBuildingPartnerId(building);
      if (partnerId) {
        initialPartners[building.id] = partnerId;
      }
    });
    setBuildingPartners(initialPartners);
  }, [buildings]);

  useEffect(() => {
    const loadPartners = async () => {
      try {
        const response = await coreApi.get("/partners/all");
        setPartners(response.data || []);
      } catch (error) {
        notification.error({ 
          message: t("partners.load_error"),
          description: error?.response?.data?.message 
        });
      }
    };

    if (isModalOpen && partners.length === 0) {
      loadPartners();
    }
  }, [isModalOpen]);

  const addCommunication = async (values) => {
    if (selectedBuildings.length === 0) {
      notification.error({ 
        message: t("emails.select_building_required"),
        duration: 4
      });
      return;
    }

    const buildingsWithoutPartner = selectedBuildings.filter(
      buildingId => !buildingPartners[buildingId]
    );

    if (buildingsWithoutPartner.length > 0) {
      notification.error({ 
        message: t("emails.partner_required_for_buildings"),
        duration: 4
      });
      return;
    }

    const results = await Promise.allSettled(
      selectedBuildings.map(async (buildingId) => {
        const payload = {
          ...values,
          id: parseInt(buildingId),
          to: contactId,
          partner_id: buildingPartners[buildingId]
        };

        try {
          await coreApi.post("/emails/create/new", payload);
          return { 
            buildingId, 
            success: true,
            building: buildings.find(b => b.id === buildingId)
          };
        } catch (error) {
          return { 
            buildingId, 
            success: false,
            building: buildings.find(b => b.id === buildingId),
            error: error?.response?.data?.message || error.message
          };
        }
      })
    );

    const successful = results.filter(r => r.value?.success);
    const failed = results.filter(r => !r.value?.success);

    if (successful.length > 0) {
      notification.success({
        message: t("emails.communication_added_success"),
        description: (
          <div>
            <div className="notification-description">{t("emails.communication_success_for")}:</div>
            <List
              size="small"
              dataSource={successful}
              renderItem={(item) => {
                const building = item.value.building;
                return (
                  <List.Item className="notification-list-item">
                    <CheckCircleOutlined className="notification-icon-success" />
                    {building?.registration_number || building?.id} - {building?.reference || building?.city || ''}
                  </List.Item>
                );
              }}
            />
          </div>
        ),
        duration: 6
      });
    }

    if (failed.length > 0) {
      notification.error({
        message: t("emails.communication_add_error"),
        description: (
          <div>
            <div className="notification-description">{t("emails.communication_failed_for")}:</div>
            <List
              size="small"
              dataSource={failed}
              renderItem={(item) => {
                const building = item.value.building;
                return (
                  <List.Item className="notification-list-item failed">
                    <div>
                      <CloseCircleOutlined className="notification-icon-error" />
                      {building?.registration_number || building?.id} - {building?.reference || building?.city || ''}
                    </div>
                    {item.value.error && (
                      <div className="notification-error-details">
                        {item.value.error}
                      </div>
                    )}
                  </List.Item>
                );
              }}
            />
          </div>
        ),
        duration: 8
      });
    }

    if (successful.length > 0) {
      onUpdate && onUpdate();
      form.resetFields();
      setText('');
      setSelectedBuildings([]);
      setBuildingPartners({});
      setModalOpen(false);
    }
  };

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setText(newText);
    form.setFieldsValue({ subject: newText });
  };

  const handleBuildingToggle = (buildingId, checked) => {
    setSelectedBuildings(prev => 
      checked ? [...prev, buildingId] : prev.filter(id => id !== buildingId)
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedBuildings(checked ? buildings.map(b => b.id) : []);
  };

  const handlePartnerChange = (buildingId, partnerId) => {
    setBuildingPartners(prev => ({
      ...prev,
      [buildingId]: partnerId
    }));
  };

  return (
    <React.Fragment>
      <Button type="primary" onClick={() => { 
        setModalOpen(true);
      }}>
        {t("emails.communication_add")}
      </Button>
      <Modal
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
          setText('');
          setSelectedBuildings([]);
          setBuildingPartners({});
        }}
        onOk={() => {
          form.submit();
        }}
        okText={t("common.add")}
        open={isModalOpen}
        title={t("emails.communication_add_title")}
        width={800}
      >
        <Form
          form={form}
          onFinish={async (values) => {
            await addCommunication(values);
          }}
          layout={"horizontal"}
        >
          <Row gutter={gutter}>
            <Col span={24}>
              <Form.Item
                label={t("emails.select_buildings")}
                required
                className="full-width-control"
              >
                <div className="buildings-list-container">
                  <div className="buildings-list-header">
                    <Checkbox
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      checked={selectedBuildings.length === buildings.length && buildings.length > 0}
                      indeterminate={selectedBuildings.length > 0 && selectedBuildings.length < buildings.length}
                    >
                      {t("common.selectAll")}
                    </Checkbox>
                  </div>
                  {buildings.map((building) => (
                    <div 
                      key={building.id} 
                      className={`building-item ${selectedBuildings.includes(building.id) ? 'selected' : ''}`}
                    >
                      <div className="building-item-content">
                        <div className="building-item-checkbox">
                          <Checkbox
                            checked={selectedBuildings.includes(building.id)}
                            onChange={(e) => handleBuildingToggle(building.id, e.target.checked)}
                          />
                        </div>
                        <div className="building-item-info">
                          <strong>{building.registration_number}</strong>
                          <div className="building-item-info-subtitle">
                            {building.reference || building.city}
                          </div>
                        </div>
                        <div className="building-item-partner">
                          <PartnerSelect
                            key={`partner-${building.id}`}
                            name={`partner_${building.id}`}
                            initialValue={buildingPartners[building.id]}
                            disabled={!selectedBuildings.includes(building.id)}
                            size="small"
                            onChange={(partner) => handlePartnerChange(building.id, partner?.id)}
                            className="no-margin-item"
                            defaultPartners={partners}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={gutter}>
            <Col span={24}>
              <Form.Item
                name={"created_at"}
                label={t("emails.date")}
                initialValue={moment()}
                rules={[{required: true}]}
              >
                <DatePicker value={moment()}/>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item 
                name="case_tag_ids"
                label={t("emails.communication")}
                rules={[{ required: true, message: t("emails.communication_required") }]}
              >
                <Select
                  mode="multiple"
                  placeholder={t("emails.communication_placeholder")}
                >
                  {communication_cases.map((type) => (
                    <Select.Option value={type} key={type}>
                      {t("emails.communication_cases." + type)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item 
                name="type_tag_ids"
                label={t("emails.communication_type")}
                rules={[{ required: true, message: t("emails.communication_type_required") }]}
              >
                <Select
                  mode="multiple"
                  placeholder={t("emails.communication_type_placeholder")}
                >
                  {communications.map((type) => (
                    <Select.Option key={type} value={type}>
                      {t("emails.types." + type)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={gutter}>
            <Col span={24}>
              <Form.Item
                name="subject"
                label={t("emails.description")}
                className="compact-error-item"
                rules={[
                  { required: true, message: t("emails.description_required") },
                  { 
                    validator: (_, value) => {
                      if (!value || value.length < MIN_CHARACTERS) {
                        return Promise.reject(new Error(t("emails.description_min_length")));
                      }
                      if (value.length > MAX_CHARACTERS) {
                        return Promise.reject(new Error(t("emails.communication_text_error")));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <TextArea
                  rows={4}
                  value={text}
                  onChange={handleTextChange}
                  showCount={{
                    formatter: ({ count }) => {
                      if (count > 0 && count < MIN_CHARACTERS) {
                        return `${count}/${MAX_CHARACTERS} ⚠️ ${t('emails.description_remaining')}`;
                      }
                      return `${count}/${MAX_CHARACTERS}`;
                    },
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={gutter}>
            <Col span={24}>
              <EmployeeSelect
                required={true}
                name={"from"}
                label={t("common.user")}
                activeUsers={true}
                placeholder={''}
                initialValue={JSON.parse(localStorage.getItem("user")).id}
              />
            </Col>
          </Row>
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default ContactAddCommunication;
